import { AdminApiError, request } from '../api/client.js'
import type {
  CoooriAdmin,
  CoooriAdminDetail,
  CoooriAdminListResponse,
  CreateCoooriAdminBody,
  UpdateCoooriAdminBody,
  DeactivateCoooriAdminBody,
  ListCoooriAdminsParams,
} from './types.js'

const ROOT = '/coach/admin/admins'

export const coooriAdminsApi = {
  list(params: ListCoooriAdminsParams = {}): Promise<CoooriAdminListResponse> {
    const query = new URLSearchParams()
    if (params.search) query.set('search', params.search)
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    if (params.offset !== undefined) query.set('offset', String(params.offset))
    const suffix = query.size > 0 ? `?${query.toString()}` : ''
    return request<CoooriAdminListResponse>(`${ROOT}${suffix}`, { method: 'GET' })
  },

  get(userId: string): Promise<CoooriAdminDetail> {
    return request<CoooriAdminDetail>(`${ROOT}/${userId}`, { method: 'GET' })
  },

  update(userId: string, body: UpdateCoooriAdminBody): Promise<CoooriAdminDetail> {
    return request<CoooriAdminDetail>(`${ROOT}/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
  },

  create(body: CreateCoooriAdminBody): Promise<CoooriAdmin> {
    return request<CoooriAdmin>(ROOT, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  resendInvite(userId: string): Promise<CoooriAdmin> {
    return request<CoooriAdmin>(`${ROOT}/${userId}/invite/resend`, {
      method: 'POST',
    })
  },

  cancelInvite(userId: string): Promise<void> {
    return request<void>(`${ROOT}/${userId}/invite/cancel`, {
      method: 'POST',
    })
  },

  deactivate(userId: string, body: DeactivateCoooriAdminBody): Promise<void> {
    return request<void>(`${ROOT}/${userId}/deactivate`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },
}

/** Stable server failures that belong beside a specific form field. */
export const COOORI_ADMIN_ERROR_FIELD = {
  email_already_registered: 'email',
} as const

export type CoooriAdminField =
  (typeof COOORI_ADMIN_ERROR_FIELD)[keyof typeof COOORI_ADMIN_ERROR_FIELD]

export function messageForCoooriAdminError(error: unknown): string {
  if (error instanceof AdminApiError) {
    switch (error.detail) {
      case 'email_already_registered':
        return 'That email address is already registered.'
      case 'admin_active_client_required':
        return 'Your admin account is not attached to an active client.'
      case 'admin_invite_not_registered':
        return 'Admin invitations are not available until the dedicated email template is published.'
      case 'invite_not_available':
        return 'This invitation can no longer be replaced because the admin is already active.'
      case 'invite_not_cancellable':
        return 'This invitation can no longer be cancelled because the admin is already active.'
      case 'rate_limited':
        return 'This address reached the three-invitations-per-hour limit. Wait for the one-hour window to clear before trying again.'
      case 'admin_not_found':
        return 'That Cooori admin no longer exists.'
      case 'admin_not_active':
        return 'This admin has not activated access. Cancel the invitation instead.'
      case 'cannot_deactivate_self':
        return 'You cannot deactivate your own admin access.'
      case 'last_active_admin':
        return 'This is the last active admin for the client. Activate another admin before removing this access.'
      case 'admin_required':
        return 'You do not have permission to manage Cooori admins.'
      default:
        return error.message
    }
  }
  return error instanceof Error ? error.message : 'Request failed'
}
